import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './EmiSection.css';
import {
  cardVariantsLeft,
  cardVariantsRight,
  cardVariants,
  hoverVariants,
  textVariants,
  listItemVariants,
  factorItemHover,
  exampleBoxHover
} from '../../utility/framerAnimations';

const EmiInfoSection = () => {
  const { t } = useTranslation('homepage');

  return (
    <Container className="emi-info-section">
      <Row className="justify-content-center w-100">
        <Col lg={12} md={12}>
          <div className="info-content">
            
            {/* Section 1: What Is an EMI Calculator? - From Left */}
            <motion.div 
              className="info-card "
              variants={cardVariantsLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                {t('emi-info-title-1')}
              </motion.h2>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                {t('emi-info-text-1')}
              </motion.p>
            </motion.div>

            {/* Section 2: How Does an EMI Calculator Work? - From Right */}
            <motion.div 
              className="info-card"
              variants={cardVariantsRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                {t('emi-info-title-2')}
              </motion.h2>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                {t('emi-info-text-2')}
              </motion.p>
            </motion.div>

            {/* Section 3: How To Use Our Loan EMI Calculator? - From Left */}
            <motion.div 
              className="info-card "
              variants={cardVariantsLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                {t('emi-info-title-3')}
              </motion.h2>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                {t('emi-info-text-3')}
              </motion.p>
              <motion.ul 
                className="steps-list"
              >
                <motion.li 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  {t('emi-info-step-1')}
                </motion.li>
                <motion.li 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  {t('emi-info-step-2')}
                </motion.li>
                <motion.li 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                >
                  {t('emi-info-step-3')}
                </motion.li>
              </motion.ul>
              <motion.p 
                className="section-text"
                variants={textVariants}
              >
                {t('emi-info-text-4')}
              </motion.p>
              
              {/* Example Section */}
              <motion.div 
                className="example-box mt-4"
                variants={cardVariants}
                whileHover="hover"
                hoverVariants={exampleBoxHover}
              >
                <motion.h4 
                  className="example-heading"
                  variants={textVariants}
                >
                  {t('emi-info-example-heading')}
                </motion.h4>
                <motion.p 
                  className="section-text"
                  variants={textVariants}
                >
                  {t('emi-info-example-text')}
                </motion.p>
                <motion.ul 
                  className="example-list"
                >
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <strong>{t('emi-info-example-emi-label')}</strong> ₹3,705/month
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <strong>{t('emi-info-example-principal-label')}</strong> ₹1,20,000
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <strong>{t('emi-info-example-interest-label')}</strong> ₹13,389
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 1.1 }}
                  >
                    <strong>{t('emi-info-example-total-label')}</strong> ₹1,33,389
                  </motion.li>
                </motion.ul>
              </motion.div>
            </motion.div>

            {/* Section 4: Factors Affecting Your EMI Amount - From Right */}
            <motion.div 
              className="info-card"
              variants={cardVariantsRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover="hover"
              hoverVariants={hoverVariants}
            >
              <motion.h2 
                className="section-heading"
                variants={textVariants}
              >
                {t('emi-info-title-4')}
              </motion.h2>
              <motion.div 
                className="factors-list"
              >
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-interest-rate-title')}
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-interest-rate-text')}
                  </motion.p>
                </motion.div>
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-loan-tenure-title')}
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-loan-tenure-text')}
                  </motion.p>
                </motion.div>
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-interest-type-title')}
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-interest-type-text')}
                  </motion.p>
                </motion.div>
                <motion.div 
                  className="factor-item"
                  variants={cardVariants}
                  whileHover="hover"
                  hoverVariants={factorItemHover}
                >
                  <motion.h5 
                    className="factor-title"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-credit-score-title')}
                  </motion.h5>
                  <motion.p 
                    className="factor-text"
                    variants={textVariants}
                  >
                    {t('emi-info-factor-credit-score-text')}
                  </motion.p>
                </motion.div>
              </motion.div>
            </motion.div>

          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmiInfoSection; 